import { useEffect, useRef, useState } from "react";
import { EventSourcePolyfill } from "event-source-polyfill";
interface SSEData {
  count: number;
  data: any[];
  statusCount: any;
  timestamp: string;
  lastUpdated?: string;
  eventType?: string;
  eventDetails?: any;
  error?: string;
}

interface UseSSEOptions {
  enabled?: boolean;
  reconnectAttempts?: number;
  reconnectInterval?: number;
  onError?: (error: Event) => void;
  onOpen?: (event: Event) => void;
  onMessage?: (data: SSEData) => void;
}

export const useSSE = (url: string, options: UseSSEOptions = {}) => {
  const {
    enabled = true,
    reconnectAttempts = 3,
    reconnectInterval = 5000,
    onError,
    onOpen,
    onMessage,
  } = options;

  const [data, setData] = useState<SSEData | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSourcePolyfill | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = () => {
    if (!enabled || eventSourceRef.current) return;

    setIsConnecting(true);
    setError(null);

    try {
      // const eventSource = new EventSource(url);
      const tokenStr = localStorage.getItem("token");
      const token = tokenStr ? JSON.parse(tokenStr) : null;
      const eventSource = new EventSourcePolyfill(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        heartbeatTimeout: 600000,
      });
      eventSourceRef.current = eventSource;

      eventSource.onopen = (event) => {
        setIsConnecting(false);
        setIsConnected(true);
        setError(null);
        reconnectCountRef.current = 0;
        onOpen?.(event);
        console.log("✅ SSE Connected to:", url);
      };

      eventSource.onmessage = (event) => {
        try {
          const parsedData: SSEData = JSON.parse(event.data);
          setData(parsedData);
          onMessage?.(parsedData);

          // Log real-time updates
          if (parsedData.eventType && parsedData.eventType !== "initial") {
            console.log("🔄 SSE Update:", parsedData.eventType, parsedData.lastUpdated);
          }
        } catch (err) {
          console.error("❌ Failed to parse SSE data:", err);
        }
      };

      eventSource.onerror = (event) => {
        setIsConnecting(false);
        setIsConnected(false);

        const errorMsg = `SSE connection error: ${eventSource.readyState}`;
        setError(errorMsg);
        onError?.(event);

        // Auto reconnect logic
        if (reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current++;
          console.log(`🔄 Reconnecting SSE (${reconnectCountRef.current}/${reconnectAttempts})...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            disconnect();
            connect();
          }, reconnectInterval);
        } else {
          console.error("❌ SSE max reconnection attempts reached");
        }
      };
    } catch (err) {
      setIsConnecting(false);
      setError(err instanceof Error ? err.message : "Unknown error");
      console.error("❌ Failed to create SSE connection:", err);
    }
  };

  const disconnect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
  };

  const reconnect = () => {
    disconnect();
    reconnectCountRef.current = 0;
    connect();
  };

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled]);

  return {
    data,
    isConnecting,
    isConnected,
    error,
    reconnect,
    disconnect,
  };
};
